import {
  BeneficialOwnerControlType,
  useUpdateBeneficialOwner,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@commenda/kakashi';
import { useEffect, useState } from 'react';
import { friendlyOwnershipMessage } from '../forms/applyOwnershipApiError';
import { ControlTypePicker } from '../forms/ControlTypePicker';

interface EditUboControlTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  corporationId: number;
  directorId?: number;
  currentControlType?: BeneficialOwnerControlType | null;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export const EditUltimateBeneficialOwnerControlTypeModal = ({
  isOpen,
  onClose,
  corporationId,
  directorId,
  currentControlType,
  onSuccess,
  onError,
}: EditUboControlTypeModalProps) => {
  const mutation = useUpdateBeneficialOwner(corporationId);
  const [selected, setSelected] = useState<BeneficialOwnerControlType | undefined>(
    currentControlType ?? undefined,
  );

  useEffect(() => {
    if (isOpen) setSelected(currentControlType ?? undefined);
  }, [isOpen, currentControlType]);

  const handleSave = async () => {
    if (!directorId || !selected) return;
    try {
      await mutation.mutateAsync({
        directorId,
        body: { effectiveControlType: selected },
      });
      onSuccess?.('UBO control type updated.');
      onClose();
    } catch (err) {
      onError?.(friendlyOwnershipMessage(err));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Edit UBO control type</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-md p-3xl">
          <p className="txt-small-regular text-text-secondary">
            Choose how this beneficial owner exercises significant control over
            the entity.
          </p>
          <ControlTypePicker
            value={selected}
            onChange={setSelected}
            placeholder="Select a control type"
          />
        </div>
        <DialogFooter>
          <Button variant="secondary" size="md" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            type="button"
            onClick={handleSave}
            isLoading={mutation.isPending}
            loadingLabel="Saving…"
            disabled={!selected || selected === currentControlType}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
